import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
} from "react";
import { tokens } from "../../design-system/tokens";
import Menu3Dots from "./Menu3Dots";
import styles from "./Menu3DotsButton.module.css";

export type Menu3DotsButtonColor = "light" | "secondary" | "primary";

/** Matches `UiElements/Button` heights. */
export type Menu3DotsButtonSize = "small" | "medium" | "large";

const SIZE_PX: Record<Menu3DotsButtonSize, number> = {
  small: 25,
  medium: 36,
  large: 40,
};

type ColorPalette = {
  bg: string;
  dots: string;
  bgHover: string;
  dotsHover: string;
};

const COLOR_PALETTES: Record<Menu3DotsButtonColor, ColorPalette> = {
  light: {
    bg: tokens.colors.background,
    dots: tokens.colors.primary,
    bgHover: tokens.buttonColors.secondaryLightHover,
    dotsHover: tokens.buttonColors.primary,
  },
  secondary: {
    bg: tokens.buttonColors.secondary,
    dots: tokens.colors.text.inverse,
    bgHover: tokens.buttonColors.secondaryHover,
    dotsHover: tokens.colors.text.inverse,
  },
  primary: {
    bg: tokens.buttonColors.primary,
    dots: tokens.colors.text.inverse,
    bgHover: tokens.buttonColors.primaryHover,
    dotsHover: tokens.colors.text.inverse,
  },
};

export interface Menu3DotsButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  color?: Menu3DotsButtonColor;
  /** Pixel size override. Prefer `buttonSize` to stay aligned with `Button`. */
  size?: number;
  buttonSize?: Menu3DotsButtonSize;
}

const Menu3DotsButton = forwardRef<HTMLButtonElement, Menu3DotsButtonProps>(
  function Menu3DotsButton(
    {
      color = "light",
      size,
      buttonSize = "small",
      className,
      disabled = false,
      type = "button",
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref,
  ) {
    const [hovered, setHovered] = useState(false);
    const palette = COLOR_PALETTES[color];
    const isActive = hovered && !disabled;

    const pixelSize = size ?? SIZE_PX[buttonSize];

    const mergedClassName = ["ui-elements-button", styles.root, className]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={mergedClassName}
        style={{
          width: pixelSize,
          height: pixelSize,
          minWidth: pixelSize,
          minHeight: pixelSize,
        }}
        onMouseEnter={(event) => {
          setHovered(true);
          onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          setHovered(false);
          onMouseLeave?.(event);
        }}
        {...rest}
      >
        <Menu3Dots
          backgroundColor={isActive ? palette.bgHover : palette.bg}
          dotsColor={isActive ? palette.dotsHover : palette.dots}
        />
      </button>
    );
  },
);

Menu3DotsButton.displayName = "Menu3DotsButton";

export default Menu3DotsButton;
